const BOUNDARY = "data-google-translate-text";
let nextBoundary = 0;
const EXCLUDED =
  `[${BOUNDARY}], .notranslate, [translate="no"], .skiptranslate, ` +
  "#google_translate_element, [data-google-translate-bank], [data-google-translate-probe], [class*='VIpgJd-'], script, style, noscript, " +
  "textarea, input, select, option, svg, math, [contenteditable]:not([contenteditable='false'])";

/**
 * Google may replace a Text node with <font> elements. React retains the original
 * Text reference, so merely ignoring removeChild errors leaves stale translations.
 * Keep a DOM copy in a stable boundary and redirect only operations on React's
 * retained source nodes. Google owns the contents; React still owns their place
 * among its siblings. Unrelated DOM operations retain their native error behavior.
 */
export function installTranslationDomBridge() {
  const boundaries = new WeakMap<Node, HTMLSpanElement>();
  const sources = new WeakMap<HTMLSpanElement, Text>();
  const sourceIds = new Map<string, WeakRef<Text>>();
  const sourceCleanup = new FinalizationRegistry<string>((id) =>
    sourceIds.delete(id),
  );
  const removeChild = Node.prototype.removeChild;
  const insertBefore = Node.prototype.insertBefore;
  const appendChild = Node.prototype.appendChild;
  const nodeValue = Object.getOwnPropertyDescriptor(
    Node.prototype,
    "nodeValue",
  )!;
  const textContent = Object.getOwnPropertyDescriptor(
    Node.prototype,
    "textContent",
  )!;
  let disposed = false;

  function boundaryFor(node: Node): HTMLSpanElement | undefined {
    let boundary = boundaries.get(node);
    if (boundary && !boundary.isConnected) {
      // Google can restore a clone of the original span. Keep the identity on
      // the DOM boundary so React's original Text reference can find that clone.
      const id = boundary.getAttribute(BOUNDARY);
      const restored = document.querySelector<HTMLSpanElement>(
        `[${BOUNDARY}="${id}"]`,
      );
      if (restored) {
        boundary = restored;
        boundaries.set(node, restored);
        sources.set(restored, node as Text);
      }
    }
    return boundary;
  }

  function visibleNode(node: Node, parent: Node): Node {
    // Google's own moves inside its boundary must remain native operations.
    if (
      parent instanceof Element &&
      (parent.closest(`[${BOUNDARY}]`) || parent.tagName === "FONT")
    )
      return node;
    return boundaryFor(node) ?? node;
  }

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    const boundary = boundaryFor(child);
    if (boundary?.parentNode === this) {
      removeChild.call(this, boundary);
      return child;
    }
    return removeChild.call(this, child) as T;
  };
  Node.prototype.insertBefore = function <T extends Node>(
    child: T,
    before: Node | null,
  ): T {
    const boundary = before && boundaryFor(before);
    const reference =
      before?.parentNode !== this && boundary?.parentNode === this
        ? boundary
        : before;
    insertBefore.call(this, visibleNode(child, this), reference);
    return child;
  };
  Node.prototype.appendChild = function <T extends Node>(child: T): T {
    appendChild.call(this, visibleNode(child, this));
    return child;
  };

  function updateSource(
    node: Node,
    value: string | null,
    descriptor: PropertyDescriptor,
  ) {
    descriptor.set!.call(node, value);
    const boundary = boundaryFor(node);
    if (boundary) {
      // A fresh child is also observable by Google's incremental translator.
      textContent.set!.call(boundary, value ?? "");
    }
  }
  Object.defineProperty(Node.prototype, "nodeValue", {
    ...nodeValue,
    set(value: string | null) {
      updateSource(this, value, nodeValue);
    },
  });
  Object.defineProperty(Node.prototype, "textContent", {
    ...textContent,
    set(value: string | null) {
      if (this.nodeType === Node.TEXT_NODE)
        updateSource(this, value, textContent);
      else textContent.set!.call(this, value);
    },
  });

  function protectText(node: Text) {
    const parent = node.parentElement;
    if (!parent || !node.data.trim() || parent.closest(EXCLUDED)) return;
    const boundary = document.createElement("span");
    const id = String(++nextBoundary);
    boundary.setAttribute(BOUNDARY, id);
    // A real box lets Google's incremental observer see later updates. An
    // atomic inline box also keeps Google from merging sibling React nodes into
    // a compound translation and restoring cloned wrappers on the next switch.
    boundary.style.display = "inline-block";
    const whiteSpace = getComputedStyle(parent).whiteSpace;
    boundary.style.whiteSpace =
      !whiteSpace || whiteSpace === "normal" ? "break-spaces" : "inherit";
    boundary.style.maxWidth = "100%";
    // An atomic child hides the parent's text-overflow ellipsis. Carry the
    // truncation onto the boundary that now contains the actual text.
    boundary.style.textOverflow = "inherit";
    const style = getComputedStyle(parent);
    if (style.textOverflow === "ellipsis") {
      boundary.style.overflow = "hidden";
      boundary.style.verticalAlign = "bottom";
    }
    // Google must see a copy, never React's retained Text reference. Otherwise
    // Google's own nodeValue writes look like source changes and its restore
    // cache can detach the very node React will update or remove next.
    insertBefore.call(parent, boundary, node);
    appendChild.call(boundary, document.createTextNode(node.data));
    removeChild.call(parent, node);
    boundaries.set(node, boundary);
    sources.set(boundary, node);
    sourceIds.set(id, new WeakRef(node));
    sourceCleanup.register(node, id);
  }

  function protectTree(root: Node) {
    if (root.nodeType === Node.TEXT_NODE) {
      protectText(root as Text);
      return;
    }
    if (!(root instanceof Element) || root.closest(EXCLUDED)) return;
    // Editable values belong to their input/editor, not to the page translator.
    // In particular, translating MUI's date sections corrupts displayed values
    // when the adapter changes section order for the selected locale.
    for (const editable of root.querySelectorAll(
      '[contenteditable]:not([contenteditable="false"])',
    ))
      editable.setAttribute("translate", "no");
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    while (walker.nextNode()) nodes.push(walker.currentNode as Text);
    nodes.forEach(protectText);
  }

  const observer = new MutationObserver((records) => {
    if (disposed) return;
    for (const record of records) {
      if (record.target instanceof Element && record.target.closest(EXCLUDED))
        continue;
      if (record.type === "childList") {
        for (const added of record.addedNodes) {
          if (
            added instanceof Element &&
            added.matches('[contenteditable]:not([contenteditable="false"])')
          )
            added.setAttribute("translate", "no");
          if (added instanceof Element && added.hasAttribute(BOUNDARY))
            continue;
          protectTree(added);
        }
      }
    }
  });
  protectTree(document.body);
  observer.observe(document.body, { childList: true, subtree: true });

  return () => {
    disposed = true;
    observer.disconnect();
    for (const boundary of document.querySelectorAll<HTMLSpanElement>(
      `[${BOUNDARY}]`,
    )) {
      const source =
        sources.get(boundary) ??
        sourceIds.get(boundary.getAttribute(BOUNDARY)!)?.deref();
      if (source && boundary.parentNode) {
        insertBefore.call(boundary.parentNode, source, boundary);
        removeChild.call(boundary.parentNode, boundary);
      }
    }
    Node.prototype.removeChild = removeChild;
    Node.prototype.insertBefore = insertBefore;
    Node.prototype.appendChild = appendChild;
    Object.defineProperty(Node.prototype, "nodeValue", nodeValue);
    Object.defineProperty(Node.prototype, "textContent", textContent);
    sourceIds.clear();
  };
}
